import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { UserRole } from '../../user/user.type';
import OnboardingStepSkillsUi from '../ui/screens/onboarding-step-skills.ui';
import { TAGS_CONFIGS, getDegrees, getDevDegrees } from '../onboarding.service';

import { useContext, useEffect, useState, useCallback } from 'react';
import { RootStoreContext } from '../../core/root-store';
import { OnboardingData, OnboardingStep } from '../onboarding.type';

const OnboardingStepSkillsContainer = observer(() => {
  const rootStore = useContext(RootStoreContext);
  const store = rootStore.onboardingStore;
  const { role } = store.formData;
  const stepErrors = store.shouldShowStepErrors;

  const [educationForm, setEducationForm] = useState({
    level: '',
    title: '',
    institution: '',
  });

  const [engineerSkillsTags, setEngineerSkillsTags] = useState<string[]>([]);
  const [founderSkillsTags, setFounderSkillsTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tagConfigs = {
    devSkills: TAGS_CONFIGS.DEV_DEGREES,
    founderSkills: TAGS_CONFIGS.DEGREES,
  };

  useEffect(() => {
    const fetchRoleSpecificTags = async () => {
      if (!role) return;

      try {
        setLoading(true);

        if (role === UserRole.Founder) {
          const fSkillsTags = await getDegrees();

          setFounderSkillsTags(fSkillsTags);
        } else if (role === UserRole.SoftwareEngineer) {
          const eSkills = await getDevDegrees();
          setEngineerSkillsTags(eSkills);
        }
      } catch (error) {
        const errorMessage = 'Failed to load options';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchRoleSpecificTags();
  }, [role]);

  const addDegree = useCallback(async () => {
    const { level, title, institution } = educationForm;
    const success = store.updateDegreeField('add', undefined, level, title, institution);

    if (success) {
      setEducationForm({ level: '', title: '', institution: '' });
    }
  }, [educationForm, store]);

  const removeDegree = (degree: string) => {
    store.updateDegreeField('remove', degree);
  };

  const updateEducationForm = (field: keyof typeof educationForm, value: string) => {
    setEducationForm(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (store.step === OnboardingStep.Skills && store.hasAttemptedCurrentStep) {
      const hasDegrees = (store.formData.degrees || []).length > 0;
      const { level, title, institution } = educationForm;

      if (!hasDegrees) {
        if (level && title.trim() && institution.trim()) {
          // Attempt to add degree
          const success = store.updateDegreeField('add', undefined, level, title, institution);
          if (success) {
            setEducationForm({ level: '', title: '', institution: '' });
          }
        } else {
          // Trigger validation errors for incomplete form
          store.updateDegreeField('add', undefined, level, title, institution);
        }

        if (store.continueable) {
          (async () => {
            await store.saveCurrentStepData();
            const next = store.nextStep;
            if (next) {
              store.updateOnboardingStep(next);
            }
          })();
        }
      }
    }
  }, [store.step, store.hasAttemptedCurrentStep, store.formData.degrees, educationForm, store]);

  const updateArrayField = (field: keyof OnboardingData, action: 'add' | 'remove', value: string) => {
    store.updateArrayField(field, action, value);
  };

  return (
    <OnboardingStepSkillsUi
      role={store.formData.role!}
      engineerSkillsTags={engineerSkillsTags}
      skills={store.formData.skills}
      skillsLoading={loading}
      engineerSkillsMaxSelection={tagConfigs.devSkills.maxSelection}
      founderSkillsTags={founderSkillsTags}
      founderSkillsMaxSelection={tagConfigs.founderSkills.maxSelection}
      notableCompanies={store.formData.notableCompanies}
      qualificationLevel={educationForm.level}
      qualificationTitle={educationForm.title}
      qualificationInstitution={educationForm.institution}
      degrees={store.formData.degrees}
      experience={store.formData.yearsOfExperience}
      isStudent={store.formData.isStudent}

      skillsFetchError={error}
      skillsError={stepErrors.skills}
      notableCompaniesError={store.errors.notableCompanies}
      educationFormErrors={store.errors.educationForm}
      degreesError={store.errors.degrees || stepErrors.degrees}

      onUpdateField={(field, action, value) => {
        if (field === 'qualificationLevel') {
          updateEducationForm('level', value);
        } else if (field === 'qualificationTitle') {
          updateEducationForm('title', value);
        } else if (field === 'qualificationInstitution') {
          updateEducationForm('institution', value);
        } else if (field === 'yearsOfExperience') {
          store.updateField('yearsOfExperience', parseInt(value) || 0);
        } else if (field === 'isStudent') {
          store.updateBooleanField('isStudent');
        } else {
          updateArrayField(field as keyof OnboardingData, action, value);
        }
      }}
      onUpdateBooleanField={() => store.updateBooleanField('isStudent')}
      onAddDegrees={addDegree}
      onRemoveDegrees={removeDegree}
    />
  );
});

export default OnboardingStepSkillsContainer;
